import type { SlideSet } from '../../../api/slide-set';
import type { Node } from '../../../api/table-of-content';
import type { Module } from '../../../api/module';
import type { NodeWithoutDescription } from '../../../api/table-of-content';
import type { ValuesOfObject } from '../../../@types/utils';
import type { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../constants/analyticsEvents';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import List from '@pelckmans/elementis/components/list';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import Details from '@pelckmans/elementis/icons/details';
import Duplicate from '@pelckmans/elementis/icons/duplicate';
import Edit from '@pelckmans/elementis/icons/edit';
import Share from '@pelckmans/elementis/icons/share';
import Trash from '@pelckmans/elementis/icons/trash';
import Print from '@pelckmans/elementis/icons/print';
import MaterialPresentationPresentModusIcon from '@pelckmans/elementis/icons/material-presentation-present-modus';
import useAnalytics from '../../../hooks/useAnalytics';
import useSlideSets from '../hooks/useSlideSets';
import IdentityContext from '../../../contexts/IdentityContext';
import SelectionContext from '../toc/_contexts/SelectionContext';
import ModalContext from '../../../contexts/ModalContext';
import MaterialPills from '../toc/components/MaterialPills';
import PinnedMaterialButton from '../toc/components/pinned-material/PinnedMaterialButton';
import ActionMenuButton from '../../../shared-components/ActionMenuButton';
import ShareModal from '../../../shared-components/share-modal/ShareModal';
import { MaterialIcon } from './MaterialIcon';
import ConfirmationModal from '../../../shared-components/ConfirmationModal';
import { ANALYTICS_EVENT_TYPES } from '../../../constants/analyticsEvents';
import useLicenses from '../../../hooks/useLicenses';

type Props = {
  node: Node | NodeWithoutDescription | undefined;
  module: Module;
  slideSet: SlideSet;
  canBePinned?: boolean;
  subLocationId: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
  refetch: () => void;
  isShared?: boolean;
};

export default function SlideSetListItem({
  node: selectedNode,
  module: selectedModule,
  slideSet,
  canBePinned,
  subLocationId,
  refetch,
  isShared = false,
}: Props) {
  const { t } = useTranslation();
  const { capture } = useAnalytics();
  const {
    buildDoceoUrl,
    slideSets,
    captureOpenSlideSetEvents,
    buildCopySlideSetUrl,
    deleteUserSlideSet,
    setSlideSets,
  } = useSlideSets();

  const { identity } = useContext(IdentityContext);
  const { showSelection, selectedSlideSets, addSlideSets, removeSlideSets } = useContext(SelectionContext);
  const { showModal } = useContext(ModalContext);
  const { isModuleLicensed } = useLicenses();

  const slideSetUrl = buildDoceoUrl(slideSet);
  const slideSetOverviewUrl = buildDoceoUrl(slideSet, false);
  const slideSetEditUrl = buildDoceoUrl(slideSet, true);
  const disabled = !isModuleLicensed(selectedModule);

  const pillGroupProps = {
    'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
    children: <MaterialPills isShared={isShared} />,
    role: 'group',
  };

  function captureEditSlideSetEvents(slideSetId: number) {
    capture({
      objectId: slideSetId.toString(),
      eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_ACCESSED,
      subLocationId,
    });

    capture({
      objectId: slideSetId.toString(),
      eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_EDIT_MODE_ACCESSED,
      subLocationId,
    });
  }

  function captureOpenSlideSetOverviewEvents(slideSetId: number) {
    capture({
      objectId: slideSetId.toString(),
      eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_ACCESSED,
      subLocationId,
    });

    capture({
      objectId: slideSetId.toString(),
      eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_OVERVIEW_ACCESSED,
      subLocationId,
    });
  }

  const handleDelete = async (slideSet: SlideSet) => {
    await deleteUserSlideSet(slideSet);
    refetch();
    setSlideSets(slideSets.filter(currentSlideSet => currentSlideSet.id !== slideSet.id));
    capture({
      objectId: slideSet.id.toString(),
      eventType: ANALYTICS_EVENT_TYPES.SLIDE_SET_DELETED,
      subLocationId: subLocationId,
    });
  };

  const showDeleteModal = (slideSet: SlideSet) => {
    const BASE = 'PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_SLIDE_SET.CONFIRMATION_MODAL';
    return showModal(
      <ConfirmationModal
        id={slideSet.id.toString()}
        headerTitle={t(`${BASE}.HEADER_TITLE`, {
          name: slideSet.name,
        })}
        title={t(`${BASE}.TITLE`)}
        message={t(`${BASE}.BODY`)}
        confirmationText={t(`${BASE}.CONFIRMATION_TEXT`)}
        onConfirm={() => handleDelete(slideSet)}
      />,
    );
  };

  const showShareModal = (slideset: SlideSet) => {
    if (!selectedNode) return;
    showModal(
      <ShareModal
        id="share-modal"
        moduleId={selectedModule.id}
        nodeId={selectedNode.id}
        slideSets={[slideset]}
        onSubmitted={refetch}
      />,
    );
  };

  function ActionsMenu() {
    if (!identity.isTeacherAlike) return null;

    return (
      <ActionMenuButton
        disabled={disabled}
        menuProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.A11Y_LABEL'),
        }}
        buttonProps={{
          ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.BUTTON.A11Y_LABEL'),
        }}
      >
        {handleToggle => (
          <>
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.PRESENT_SLIDE_SET.LABEL')}
              textIcon={<MaterialPresentationPresentModusIcon />}
              role="menuitem"
              href={slideSetUrl}
              target="_blank"
              rel="noopener noreferrer"
              as="link"
              onClick={() => captureOpenSlideSetEvents(slideSet.id, subLocationId)}
            />
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.OVERVIEW_SLIDE_SET.LABEL')}
              textIcon={<Details />}
              role="menuitem"
              href={slideSetOverviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              as="link"
              showSeparator={true}
              onClick={() => captureOpenSlideSetOverviewEvents(slideSet.id)}
            />
            {slideSet.userId && (
              <DropdownMenu.Item
                text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.EDIT_SLIDE_SET.LABEL')}
                textIcon={<Edit />}
                role="menuitem"
                href={slideSetEditUrl}
                target="_blank"
                rel="noopener noreferrer"
                as="link"
                onClick={() => {
                  captureEditSlideSetEvents(slideSet.id);
                  handleToggle();
                }}
              />
            )}
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DUPLICATE_SLIDE_SET.LABEL')}
              textIcon={<Duplicate />}
              role="menuitem"
              href={buildCopySlideSetUrl(
                selectedModule.id,
                slideSet.nodeId,
                slideSet.id,
                slideSet.versionId,
                subLocationId,
              )}
              target="_blank"
              rel="noopener noreferrer"
              as="link"
              onClick={() => handleToggle()}
            />
            <DropdownMenu.Item
              text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.PRINT_SLIDE_SET.LABEL')}
              textIcon={<Print />}
              role="menuitem"
              href={`${slideSetOverviewUrl}open-print-modal=true`}
              target="_blank"
              rel="noopener noreferrer"
              as="link"
              onClick={() => handleToggle()}
            />
            {slideSet.userId && (
              <>
                <DropdownMenu.Item
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.SHARE_MATERIAL.LABEL')}
                  textIcon={<Share />}
                  as="button"
                  role="menuitem"
                  onClick={() => {
                    showShareModal(slideSet);
                    handleToggle();
                  }}
                />
                <DropdownMenu.Item
                  prominence="danger"
                  text={t('PAGES.TOC_LEVEL.ACTION_MENU.OPTIONS.DELETE_SLIDE_SET.LABEL')}
                  textIcon={<Trash />}
                  as="button"
                  role="menuitem"
                  showSeparator={true}
                  onClick={() => {
                    showDeleteModal(slideSet);
                    handleToggle();
                  }}
                />
              </>
            )}
          </>
        )}
      </ActionMenuButton>
    );
  }

  const actionArea = (
    <>
      {canBePinned && <PinnedMaterialButton type="slide-set" materialId={slideSet.id} disabled={disabled} />}
      {!showSelection && <ActionsMenu />}
    </>
  );

  if (showSelection) {
    const isSelected = selectedSlideSets.includes(slideSet);
    return (
      <List.Item
        title={slideSet.name}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        as="checkbox"
        checked={isSelected}
        disabled={disabled}
        onClick={() => (isSelected ? removeSlideSets([slideSet]) : addSlideSets([slideSet]))}
      />
    );
  }

  return (
    <List.Item
      title={slideSet.name}
      icon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
      actionArea={actionArea}
      pillGroupProps={pillGroupProps}
      as="link"
      href={slideSetUrl}
      target="_blank"
      rel="noopener noreferrer"
      disabled={disabled}
      onClick={() => captureOpenSlideSetEvents(slideSet.id, subLocationId)}
    />
  );
}
